import { Box, Flex } from '@chakra-ui/react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Severity } from '../../interface';
import { colors } from '../../theme/colors';
import Card from '../Dashboard/Card';
import { Body } from '../Texts/Texts';

const SEVERITY_COLORS: Record<string, string> = {
  Severe: colors.error,
  'Moderately Severe': colors.warning,
  Moderate: colors.accent,
  Mild: colors.gray
};

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0 ? (
    <text
      x={x}
      y={y}
      fill={colors.gray}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline='central'
      fontSize={12}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

interface SeverityPieChartProps {
  severity: Severity;
  isDownloading?: boolean;
}

const SeverityPieChart = ({
  severity,
  isDownloading
}: SeverityPieChartProps) => {
  if (!severity) return null;

  const data = [
    { name: 'Severe', value: severity.severe || 0 },
    { name: 'Moderately Severe', value: severity.moderatelySevere || 0 },
    { name: 'Moderate', value: severity.moderate || 0 },
    { name: 'Mild', value: severity.mild || 0 }
  ].filter((d) => d.value > 0);

  return (
    <Card
      h='auto'
      py='24px'
      bg={isDownloading ? colors.light_green : colors.white}
    >
      <Box width='100%'>
        <Body size='16px' color='gray' mb='16px'>
          Unassigned Cases by Severity
        </Body>
        {data.length ? (
          <ResponsiveContainer width='100%' height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey='value'
                nameKey='name'
                cx='50%'
                cy='50%'
                innerRadius={60}
                outerRadius={100}
                label={renderCustomLabel}
                labelLine={false}
                isAnimationActive={true}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={SEVERITY_COLORS[entry.name]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any, name: string) => [value, name]}
                contentStyle={{
                  backgroundColor: colors.white,
                  borderRadius: 8,
                  fontSize: '13px',
                  border: `1px solid ${colors.shade}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Legend
                verticalAlign='bottom'
                height={36}
                formatter={(value: string) => (
                  <span style={{ color: colors.gray, fontSize: 12 }}>
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Flex
            justifyContent='center'
            marginX='auto'
            marginY='40px'
            width='100%'
            color={colors.gray}
          >
            No data available
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default SeverityPieChart;
